import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import GradientBar from '@/components/shared/GradientBar';

// Landing route for Google sign-in (/auth/callback). Only enabled admins may
// stay signed in; everyone else is signed out again.
const AuthCallback = () => {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const sessionFromUrl = async () => {
      const params = new URLSearchParams(window.location.search);
      const errorDesc = params.get('error_description') || params.get('error');
      if (errorDesc) throw new Error(decodeURIComponent(errorDesc));

      // PKCE flow (code parameter)
      const code = params.get('code');
      if (code) {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) throw error;
        return data;
      }

      // Implicit flow (hash with access_token)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      if (accessToken && refreshToken) {
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error) throw error;
        return data;
      }

      throw new Error('Giriş bilgisi bulunamadı');
    };

    const handleCallback = async () => {
      try {
        const data = await sessionFromUrl();
        const email = data?.user?.email;

        // Check if user is banned
        const { data: profileData } = await supabase
          .from('profiles')
          .select('is_banned')
          .eq('email', email)
          .single();

        if (profileData?.is_banned) {
          await supabase.auth.signOut();
          toast.error('Hesabınız askıya alınmıştır.', { duration: 6000 });
          navigate('/admin', { replace: true });
          return;
        }

        // Check if admin
        const { data: adminData } = await supabase
          .from('admin_users')
          .select('email')
          .eq('email', email)
          .eq('enabled', true)
          .maybeSingle();

        if (adminData) {
          toast.success('Giriş başarılı!');
          navigate('/admin/dashboard', { replace: true });
        } else {
          await supabase.auth.signOut();
          toast.error('Bu hesapla admin paneline giriş yapılamaz.');
          navigate('/admin', { replace: true });
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.message);
        setProcessing(false);
        toast.error('Giriş hatası: ' + err.message);

        setTimeout(() => {
          navigate('/admin', { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col">
      <GradientBar className="flex-1 flex flex-col items-center justify-center px-8">
        <div className="text-center">
          <h1 className="font-brand text-2xl text-brand-ink mb-4">
            {processing ? 'Giriş yapılıyor...' : 'Hata'}
          </h1>
          {processing ? (
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-ink mx-auto"></div>
          ) : (
            <p className="text-brand-red-deep">{error}</p>
          )}
        </div>
      </GradientBar>
    </div>
  );
};

export default AuthCallback;
